// PDF → 每頁 JPEG(瀏覽器內,pdfjs-dist)。移植自國中 chaiwriting-j utils/pdfToImages.js。
// kanban 106497:作文稿紙支援上傳 PDF,展開成圖片後沿用既有圖片流程。

import type * as PdfJs from "pdfjs-dist";

export const MAX_PAGES = 10;
export const TARGET_DIM = 2000; // 高於後端 normalize 的 1500px,留校正裁切餘裕
export const PDFJS_WASM_URL = "/wasm/"; // pdfjs 的 wasm 解碼元件(public/wasm/),要帶尾斜線

let pdfjsPromise: Promise<typeof PdfJs> | null = null;

// 動態載 pdfjs(只在第一次轉檔時);失敗清快取讓下次重試
export function getPdfJs(): Promise<typeof PdfJs> {
  if (!pdfjsPromise) {
    pdfjsPromise = import("pdfjs-dist")
      .then((pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = "/js/pdf.worker.min.mjs";
        return pdfjs;
      })
      .catch((e) => {
        pdfjsPromise = null;
        throw e;
      });
  }
  return pdfjsPromise;
}

function computeScale(width: number, height: number, targetDim: number): number {
  const maxSide = Math.max(width, height);
  if (!(maxSide > 0)) return 1;
  return targetDim / maxSide;
}

async function renderPageToJpeg(
  page: PdfJs.PDFPageProxy,
  targetDim: number
): Promise<{ blob: Blob; width: number; height: number }> {
  const baseViewport = page.getViewport({ scale: 1 });
  const scale = computeScale(baseViewport.width, baseViewport.height, targetDim);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("無法建立 canvas context");

  // intent: "print" 不是筆誤 —— 這裡是「把頁面輸出成圖片檔」,不是畫到畫面上給人看。
  // pdfjs 對預設的 "display" 會用 requestAnimationFrame 分批繪製,配合畫面更新節奏;
  // 而分頁一進背景,瀏覽器就凍結 rAF,render 的 promise 永遠不 resolve —— 老師上傳
  // 整班 PDF 後切到別的分頁去做事(等待時的自然行為),回來就看到「圖片載入中」轉不完。
  // "print" 走離線輸出路徑、一次畫完,不碰 rAF。
  // 實測(QA 背景分頁,A4 頁面 render 到 1413x2000):display 逾時 8 秒以上未完成,
  // print 5ms 完成(kanban 108142)。
  await page.render({ canvas, canvasContext: ctx, viewport, intent: "print" })
    .promise;

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("toBlob 失敗"))),
      "image/jpeg",
      0.9
    );
  });
  return { blob, width: canvas.width, height: canvas.height };
}

export const isPdfFile = (file: File): boolean =>
  file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");

export interface ExpandError {
  name: string;
  reason: string;
  /** "truncated" = 頁數超額截斷提示,呼叫端可據此改用自己的統一提示(kanban 1000645) */
  code?: "truncated";
  /** code === "truncated" 時,未轉出的頁數 */
  dropped?: number;
}

/**
 * 混合檔案展開:圖片原樣通過,PDF 逐頁轉成 JPEG File。
 * 頁檔名 = 原檔名(去 .pdf)+ `_頁碼.jpg`。
 *
 * 注意頁檔名**不可**當成老師的座號/姓名標註(kanban 108142):批次上傳的 PDF
 * 裝的是全班,原檔名不代表任何一位學生。初版註解寫「parseFilename 解析直接
 * 生效」是把「一生一檔」的前提誤套上來,實測會讓 AI 辨識結果被整包丟棄。
 * 呼叫端請用回傳的 fromPdf 搭配 resolveInitialAnnotation 決定初值。
 */
export async function expandFilesWithPdf(
  files: File[],
  opts?: {
    maxPages?: number;
    targetDim?: number;
    onFileProgress?: (done: number, total: number) => void;
  }
): Promise<{ files: File[]; errors: ExpandError[]; fromPdf: boolean[] }> {
  const maxPages = opts?.maxPages ?? MAX_PAGES;
  const targetDim = opts?.targetDim ?? TARGET_DIM;
  const out: File[] = [];
  const errors: ExpandError[] = [];
  // 與 out 等長:該格是不是 PDF 展開出來的頁。呼叫端據此決定要不要吃檔名
  // (resolveInitialAnnotation 的說明寫了為什麼 PDF 不能吃)
  const fromPdf: boolean[] = [];

  for (let i = 0; i < files.length; i++) {
    const f = files[i];
    if (!isPdfFile(f)) {
      out.push(f);
      fromPdf.push(false);
    } else {
      try {
        const pdfjs = await getPdfJs();
        const data = await f.arrayBuffer();
        // wasmUrl 不能省(kanban 1001709):pdfjs 5 起 CCITT/JBIG2 解碼交給 wasm,
        // 純 JS 備援檔也從同一個目錄載;沒設就整張遮罩被略過、字跡消失。
        // 影印機「壓縮 PDF」把筆跡存成 CCITT 遮罩層,轉出來只剩稿紙格線,AI 批 0 分。
        // 檔案來源 node_modules/pdfjs-dist/wasm/,升版時要一起換(同 public/js 的 worker),
        // pdfjsAssets.test.ts 會比對內容。qcms(ICC 色彩)目前不會被載入:沒設 cMapUrl /
        // standardFontDataUrl 時 useWorkerFetch 推導為 false,pdfjs 直接關掉 ICC 支援,
        // console 的「No ICC color space support」警告就是這個,與字跡無關;檔案先放著備用。
        const doc = await pdfjs.getDocument({ data, wasmUrl: PDFJS_WASM_URL }).promise;
        const take = Math.min(doc.numPages, maxPages);
        const base = (f.name || "file").replace(/\.pdf$/i, "");
        let okPages = 0;
        for (let pageNo = 1; pageNo <= take; pageNo++) {
          try {
            const page = await doc.getPage(pageNo);
            const { blob } = await renderPageToJpeg(page, targetDim);
            out.push(
              new File([blob], `${base}_${pageNo}.jpg`, { type: "image/jpeg" })
            );
            fromPdf.push(true);
            okPages++;
          } catch {
            // 單頁失敗:跳過該頁,整檔全失敗才報錯
          }
        }
        if (okPages === 0) {
          errors.push({ name: f.name, reason: "PDF 無法讀取或轉檔失敗" });
        } else if (doc.numPages > maxPages) {
          errors.push({
            name: f.name,
            code: "truncated",
            dropped: doc.numPages - maxPages,
            reason: `頁數超過 ${maxPages} 頁,僅取前 ${maxPages} 頁`,
          });
        }
      } catch {
        errors.push({ name: f.name, reason: "PDF 無法讀取或轉檔失敗" });
      }
    }
    opts?.onFileProgress?.(i + 1, files.length);
  }
  return { files: out, errors, fromPdf };
}
